import { z } from 'zod';
import { colorSchema, idSchema, mmSchema, rangeSchema } from './common.js';
import { fontRefSchema } from './fonts.js';
// ---------------------------------------------------------------------------
// Marks (A.5)
// ---------------------------------------------------------------------------
export const markSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('bold') }),
    z.object({ type: z.literal('italic') }),
    z.object({ type: z.literal('underline') }),
    z.object({ type: z.literal('font'), attrs: fontRefSchema }),
    z.object({ type: z.literal('size'), attrs: z.object({ pt: z.number().positive() }) }),
    z.object({ type: z.literal('color'), attrs: z.object({ color: colorSchema }) }),
    z.object({ type: z.literal('spacing'), attrs: z.object({ letter: z.number() }) }),
    z.object({
        type: z.literal('gap'),
        attrs: z.object({ id: idSchema, length: mmSchema.optional() }),
    }),
    z.object({ type: z.literal('solution') }),
]);
// ---------------------------------------------------------------------------
// RichText = ProseMirror-JSON (A.5)
// ---------------------------------------------------------------------------
/** Blockknoten, die der Editor erzeugt. */
export const PM_BLOCK_TYPES = ['paragraph', 'bulletList', 'orderedList', 'listItem'];
const pmTextNodeSchema = z.object({
    type: z.literal('text'),
    text: z.string(),
    marks: z.array(markSchema).optional(),
});
const pmLeafNodeSchema = z.object({ type: z.literal('hardBreak') });
const pmNodeSchema = z.lazy(() => z.union([
    pmTextNodeSchema,
    pmLeafNodeSchema,
    z.object({
        type: z.enum(PM_BLOCK_TYPES),
        attrs: z
            .looseObject({
            // TipTap schreibt nicht gesetzte Attribute als `null`; beides ist erlaubt.
            align: z.enum(['left', 'center', 'right', 'justify']).nullish(),
            indent: z.number().int().min(0).max(8).nullish(),
        })
            .optional(),
        content: z.array(pmNodeSchema).optional(),
    }),
]));
export const richTextSchema = z.object({
    type: z.literal('doc'),
    content: z.array(pmNodeSchema),
});
/** Leerer Absatz – Startwert für neue Textfelder. */
export function emptyRichText(text = '') {
    return {
        type: 'doc',
        content: [
            text ? { type: 'paragraph', content: [{ type: 'text', text }] } : { type: 'paragraph' },
        ],
    };
}
/** Reiner Text eines RichText-Dokuments, Blöcke mit \n getrennt. */
export function richTextToPlain(doc) {
    const walk = (nodes) => (nodes ?? [])
        .map((node) => {
        if (node.type === 'text')
            return node.text;
        if (node.type === 'hardBreak')
            return '\n';
        return walk(node.content);
    })
        .join('');
    return doc.content.map((node) => walk([node])).join('\n');
}
// ---------------------------------------------------------------------------
// Text-Effekte (A.5)
// ---------------------------------------------------------------------------
export const charRuleSchema = z.object({
    match: z.union([
        z.enum(['vowels', 'umlauts', 'consonants', 'first', 'last']),
        z.object({ chars: z.string(), caseSensitive: z.boolean() }),
    ]),
    position: z.enum(['any', 'start', 'end']),
    color: colorSchema,
});
export const clozeModeSchema = z.enum([
    'word',
    'wordStart',
    'wordInner',
    'wordEnd',
    'letter',
    'letterGroup',
    'punctuation',
]);
export const textEffectSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('syllableColors'),
        palette: z.array(colorSchema).min(1),
        scope: rangeSchema.optional(),
    }),
    z.object({ type: z.literal('syllableArcs') }),
    z.object({ type: z.literal('vowelDots') }),
    z.object({ type: z.literal('highlightChars'), rules: z.array(charRuleSchema) }),
    z.object({
        type: z.literal('cloze'),
        mode: clozeModeSchema,
        placement: z.enum(['random', 'manual']),
        count: z.number().int().nonnegative().optional(),
        minWordLength: z.number().int().nonnegative(),
        gapStyle: z.enum(['line', 'box', 'dots']),
        appendSolutions: z
            .object({
            order: z.enum(['original', 'alpha', 'random']),
            separator: z.string(),
        })
            .optional(),
        reseed: z.number().int().nonnegative().default(0),
    }),
    z.object({
        type: z.literal('shuffle'),
        mode: z.enum(['letters', 'inner', 'words']),
        writeLine: z.boolean(),
        reseed: z.number().int().nonnegative().default(0),
    }),
    z.object({
        type: z.literal('reverse'),
        mode: z.enum(['all', 'letters', 'words']),
        writeLine: z.boolean(),
    }),
    z.object({
        type: z.literal('wordSnake'),
        case: z.enum(['keep', 'upper', 'lower']),
        stripPunctuation: z.boolean(),
    }),
    z.object({
        type: z.literal('shuffleSentences'),
        reseed: z.number().int().nonnegative().default(0),
    }),
    z.object({ type: z.literal('case'), to: z.enum(['upper', 'lower']) }),
    z.object({ type: z.literal('mirror') }),
    z.object({ type: z.literal('eraser'), strength: z.number().min(0).max(1) }),
    z.object({ type: z.literal('strokesOnly') }),
]);
//# sourceMappingURL=text.js.map